import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';

interface ProductListItemProps {
  product: any;
  showQuickAdd?: boolean;
}

export default function ProductListItem({ product, showQuickAdd = true }: ProductListItemProps) {
  const { t } = useTranslation('shop');
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const [isAdding, setIsAdding] = useState(false);

  const defaultVariant = product.variants?.[0];
  const price = defaultVariant?.price ?? product.base_price ?? product.price ?? 0;

  async function handleAddToCart(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!defaultVariant) return;
    setIsAdding(true);
    try {
      await addItem(defaultVariant.id);
      addToast(t('addedToCart'), 'success');
    } catch {
      addToast(t('addToCartFailed'), 'error');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-4 py-3 px-4 border-b border-sage-100 last:border-b-0 hover:bg-sage-50/50 transition-colors">
      <Link
        to={`/product/${product.slug}`}
        className="flex-1 min-w-0 font-sans text-sm text-sage-900 hover:text-sage-700 transition-colors truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
      >
        {product.name}
      </Link>

      <div className="flex items-center gap-4 shrink-0">
        <span className="font-sans text-sm font-semibold text-sage-800">
          {formatCurrency(price)}
        </span>

        {showQuickAdd && defaultVariant && (
          <button
            onClick={handleAddToCart}
            disabled={isAdding}
            aria-label={t('addToCartAriaLabel', { name: product.name })}
            aria-busy={isAdding}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
              'bg-cta text-cta-fg hover:bg-cta-deep transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              'disabled:opacity-50'
            )}
          >
            <ShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />
            {isAdding ? t('adding') : t('addToCartShort')}
          </button>
        )}
      </div>
    </div>
  );
}
